// src/components/navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Database,
  ArrowLeftRight,
  UserCircle,
  LogOut,
  Upload,
} from 'lucide-react';

const AUTHENTICATOR_IMAGE = '/authenticator-icon.png';

function ProvinxLogo() {
  return (
    <div className="flex items-center gap-2 select-none">
      <img src="/provinyx-logo.png" alt="Provinyx" className="h-9 w-auto object-contain" />
      <span className="text-white font-bold text-xl tracking-widest uppercase">
        PROVINYX
      </span>
    </div>
  );
}

const ROLE_MENU_ACCESS = {
  'Distributor':   ['dashboard', 'master-data', 'transaction', 'profile'],
  'Retailer':      ['dashboard', 'master-data', 'transaction', 'profile'],
  'Repair/Refurb': ['dashboard', 'master-data', 'transaction', 'profile'],
  'Recycler':      ['dashboard', 'master-data', 'transaction', 'profile'],
  'Regulator':     ['dashboard', 'master-data', 'transaction', 'profile'],
  'Customer':      ['dashboard', 'master-data', 'transaction', 'profile'],
  'End User':      ['dashboard', 'master-data', 'transaction', 'profile'],
};

const ALL_MENU_ITEMS = [
  { id: 'dashboard',     label: 'Dashboard',     Icon: LayoutDashboard, path: '/dashboard',        navigates: true },
  { id: 'master-data',   label: 'Master Data',   Icon: Database,        path: '/master-data',      navigates: true },
  { id: 'transaction',   label: 'Transaction',   Icon: ArrowLeftRight,  path: '/transaction-data', navigates: true },
  { id: 'authenticator', label: 'Authenticator', Icon: null,            path: '/authenticator',    navigates: true, useCustomIcon: true },
  { id: 'upload-data',   label: 'Upload Data',   Icon: Upload,          path: '/upload-data',      navigates: true, manufacturerOnly: true },
  { id: 'upload-data-2', label: 'Upload Data 2', Icon: Upload,          path: '/upload-data-2',    navigates: true, manufacturerOnly: true },
  { id: 'profile',       label: 'Profile',       Icon: UserCircle,      path: '/profile',          navigates: true },
];

function getUserInfo() {
  try {
    const idToken = localStorage.getItem('id_token');
    if (idToken) {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return {
        username: payload.email?.split('@')[0] || 'User',
        email: payload.email || 'user@example.com',
        role: payload.role || payload.type || '',
      };
    }
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      const parsed = JSON.parse(storedUserData);
      return {
        username: parsed.username || parsed.email?.split('@')[0] || 'User',
        email: parsed.email || 'user@example.com',
        role: parsed.role || parsed.type || '',
      };
    }
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      return {
        username: payload.email?.split('@')[0] || 'User',
        email: payload.email || 'user@example.com',
        role: payload.type || payload.role || '',
      };
    }
  } catch { /* ignore */ }
  return { username: 'User', email: 'user@example.com', role: '' };
}

function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [activeMenu, setActiveMenu]                     = useState('dashboard');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen]         = useState(false);
  const profileDropdownRef                              = useRef(null);
  const [userData]                                      = useState(getUserInfo);

  const userRole       = userData.role || 'Manufacturer';
  const isManufacturer = userRole === 'Manufacturer';
  const allowedIds     = isManufacturer ? null : (ROLE_MENU_ACCESS[userRole] || ['dashboard', 'profile']);

  const menuItems = ALL_MENU_ITEMS.filter(item => {
    if (item.manufacturerOnly) return isManufacturer;
    return allowedIds === null || allowedIds.includes(item.id);
  });

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/dashboard') || path === '/')                        setActiveMenu('dashboard');
    else if (path.includes('/master-data') || path.includes('/product-data')) setActiveMenu('master-data');
    else if (path.includes('/transaction-data'))                             setActiveMenu('transaction');
    else if (path.includes('/authenticator'))                                setActiveMenu('authenticator');
    else if (path === '/upload-data-2')                                      setActiveMenu('upload-data-2');
    else if (path === '/upload-data')                                        setActiveMenu('upload-data');
    else if (path.includes('/profile'))                                      setActiveMenu('profile');
  }, [location]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    }
    if (isProfileDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  const handleMenuClick = (item, isMobile = false) => {
    if (!item.navigates) return;
    setActiveMenu(item.id);
    if (item.id === 'profile' && !isMobile) {
      setIsProfileDropdownOpen((prev) => !prev);
    } else {
      setIsProfileDropdownOpen(false);
      setIsMobileMenuOpen(false);
      if (item.path) navigate(item.path);
    }
  };

  const handleLogout = () => {
    [
      'userData', 'userEmail', 'user', 'username',
      'id_token', 'access_token', 'authToken',
      'pending_email', 'pending_password',
    ].forEach((k) => localStorage.removeItem(k));
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
    navigate('/login');
  };

  const renderIcon = (item, isActive, isMobile = false) => {
    const size = isMobile ? 20 : 22;
    if (item.useCustomIcon) {
      return AUTHENTICATOR_IMAGE ? (
        <img
          src={AUTHENTICATOR_IMAGE}
          alt="Authenticator"
          className={isMobile ? 'w-5 h-5 object-contain' : 'w-6 h-6 object-contain'}
          style={{
            filter: isMobile
              ? isActive
                ? 'brightness(0) saturate(100%) invert(33%) sepia(93%) saturate(1352%) hue-rotate(197deg) brightness(95%) contrast(101%)'
                : 'brightness(0) saturate(100%) invert(45%)'
              : 'brightness(0) invert(1)',
          }}
        />
      ) : null;
    }
    const { Icon } = item;
    return (
      <Icon
        size={size}
        strokeWidth={isActive ? 2.5 : 1.75}
        color={isMobile ? (isActive ? '#2563eb' : '#6b7280') : 'white'}
      />
    );
  };

  return (
    <nav className="bg-blue-600 px-4 sm:px-6 py-3 shadow-md relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1">
          <ProvinxLogo />
        </div>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {menuItems.map((item) => {
            const isActive   = activeMenu === item.id;
            const isDisabled = !item.navigates;
            return (
              <div
                key={item.id}
                className="relative"
                ref={item.id === 'profile' ? profileDropdownRef : null}
              >
                <button
                  onClick={() => handleMenuClick(item, false)}
                  title={isDisabled ? `${item.label} (coming soon)` : item.label}
                  className={`flex flex-col items-center gap-1 transition-all duration-200 ${
                    isDisabled ? 'opacity-60 cursor-default' : 'hover:opacity-80 cursor-pointer'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    {renderIcon(item, isActive, false)}
                  </div>
                  <span className={`text-xs text-white ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {item.label}
                  </span>
                </button>

                {item.id === 'profile' && isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                          <UserCircle size={28} color="white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{userData.username}</p>
                          <p className="text-xs opacity-90 truncate">{userData.email}</p>
                          {userData.role && (
                            <p className="text-xs opacity-75 mt-0.5 truncate">{userData.role}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!isMobileMenuOpen && (
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-white font-semibold text-xl"
            aria-label="Open menu"
          >
            ☰
          </button>
        )}
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 mt-1 bg-white shadow-lg border-t border-gray-200 z-40">
          <div className="flex justify-end px-4 py-2 bg-blue-600 border-b border-blue-700">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-white hover:bg-blue-700 rounded-md px-3 py-1 font-bold text-xl transition-colors"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <UserCircle size={22} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{userData.username}</p>
                <p className="text-xs opacity-90 truncate">{userData.email}</p>
                {userData.role && (
                  <p className="text-xs opacity-75 mt-0.5 truncate">{userData.role}</p>
                )}
              </div>
            </div>
          </div>

          <div className="py-2">
            {menuItems.map((item) => {
              const isActive   = activeMenu === item.id;
              const isDisabled = !item.navigates;
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item, true)}
                  title={isDisabled ? `${item.label} (coming soon)` : ''}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                    isActive
                      ? 'bg-blue-50 border-l-4 border-blue-600'
                      : isDisabled
                      ? 'opacity-60 cursor-default hover:bg-transparent'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {renderIcon(item, isActive, true)}
                  <span className={`text-sm ${isActive ? 'font-semibold text-blue-600' : 'text-gray-700'}`}>
                    {item.label}
                  </span>
                  {isDisabled && (
                    <span className="ml-auto text-xs text-gray-400 italic">soon</span>
                  )}
                </button>
              );
            })}

            <div className="border-t border-gray-200 mt-2 pt-2">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={18} />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;